import type { ReactElement } from 'react';

import { t } from '@/shared/i18n';
import type { SyncState } from '@/shared/offline';

import styles from './syncStatus.module.css';

/**
 * Глобальный индикатор НЕШТАТНЫХ состояний очереди. Штатные («в очереди: 1»,
 * пока связь есть) молчат — очередь доедет сама, и постоянный бейдж превратился бы
 * в шум, который перестают читать.
 *
 * Появился из B-023: индикатор очереди жил только на экране печати, и менеджер
 * в кабинете не мог узнать об ошибке отправки. Списание выглядело сделанным,
 * сервер о нём не знал, и единственным свидетелем расхождения был IndexedDB.
 *
 * Оба состояния здесь требуют человека И он может действовать прямо отсюда:
 * - ошибка отправки — индикатор сам является кнопкой повтора;
 * - «нужен вход» — сессия истекла, кнопка повтора упрётся в тот же отказ.
 *
 * Больше индикатор ничего не показывает. «Операции ждут своего автора» отсюда ушло
 * (Д4, ревизия 2026-09-08): очередь теперь синкается под любой сессией, автор едет
 * в теле операции, ждать некого — и показывать нечего.
 *
 * Это СТРОКА ШАПКИ под навигацией (AppLayout), а не плавающий слой. Слоем он стоял
 * в левом нижнем углу, в киоске — поднятый на 72px «над панелью навигации». Панель
 * потом уехала наверх, а внизу остались липкие футеры экрана печати и табло —
 * выше этого запаса и без постоянной высоты. На телефоне 360dp (полоса жеста ~32px)
 * нижний край индикатора стоял на 88px от низа, верх футера — на ~123px: ошибка
 * отправки накрывала футер на 35px, из них 27px — саму «Печать этикетки», и тап
 * по её верху попадал в индикатор (2026-09-27). В кабинете низ списков так же занят
 * липкой пагинацией.
 *
 * Тот же класс дефекта, что у тоста (`shared/ui/Toast.tsx`): внизу экрана печати
 * места нет ни при какой вёрстке. Но тост — ответ на только что сделанное действие,
 * закрывается крестиком и уходит вместе с окном отмены, а индикатор висит, пока
 * очередь не разберут, — поэтому не слой, а своя строка в шапке: занимает своё
 * место, видна при любой прокрутке и сама ничего не перекрывает. Тост киоска на время
 * жизни ложится поверх неё, как и поверх полосы навигации (toast.module.css).
 *
 * Экран печати ошибку отправки больше не дублирует (PrintPage.tsx): у него в шапке
 * остаётся только штатное — «нет сети» и «ждёт отправки».
 */
export function SyncStatus({ state }: { state: SyncState }): ReactElement | null {
  if (state.failed === 0 && !state.needsLogin) {
    return null;
  }

  return (
    <div className={styles.row} role="status">
      {/* «Нужен вход» ПЕРЕД ошибкой отправки и вместо неё по смыслу (B-030):
          пока сервер не признаёт сессию, кнопка повтора упрётся в тот же отказ,
          и предлагать её — обещать несбыточное. Раньше это состояние выглядело
          как обычное «ждёт сети»: признак вычислялся и терялся. */}
      {state.needsLogin ? (
        <span className={styles.needsLogin}>{t('network.needsLogin')}</span>
      ) : null}

      {state.failed > 0 && !state.needsLogin ? (
        <button type="button" className={styles.error} onClick={state.retry}>
          {t('network.syncError.retry')}
        </button>
      ) : null}
    </div>
  );
}
